import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import loadable from '@loadable/component';
import { DesignSystemIconColor } from '@minecraft.themes';

export type IconSize = 'xs' | 'ssm' | 'sm' | 'smm' | 'mmd' | 'md' | 'lg' | 'xl' | 'xlm' | 'spicerack' | '2xl' | '3xl';

export const iconSizes: Record<IconSize, string> = {
  xs: '0.5rem',
  ssm: '0.75rem',
  sm: '1rem',
  smm: '1.125rem',
  mmd: '1.25rem',
  md: '1.5rem',
  lg: '2rem',
  xl: '2.5rem',
  xlm: '3rem',
  spicerack: '1.25rem',
  '2xl': '5rem',
  '3xl': '7.5rem',
};

export type IconProps = {
  id?: string;
  name: string;
  alt?: string;
  inheritColor?: boolean;
  themeColor?: DesignSystemIconColor;
  hoverThemeColor?: DesignSystemIconColor;
  size?: IconSize;
  /**
   * @deprecated use the themeColor prop instead
   */
  color?: ((props: any) => any) | string;
  /** height in rem */
  height?: number | string;
  width?: number | string;
  className?: string;
  onClick?: (e?: any) => void;
  animation?: 'spin';
};

type ErrorBoundraryState = { hasError: boolean };

const ColouredIcon = styled(({ component, ...props }) => React.cloneElement(component, props))`
  height: ${({ $height }) => ($height ? `${$height}rem` : undefined)};
  width: ${({ $width }) => ($width ? `${$width}rem` : undefined)};

  ${(iconProps) => {
    const size = iconProps.$size ? iconSizes[iconProps.$size] : undefined;
    let fill;
    if (iconProps.$inheritColor) {
      // This is the preferred and modern way to use an icon color
      // Simply let it be the color of the text surrounding it by using text-color
      fill = 'currentColor';
    } else if (iconProps.theme.designSystem.iconColors[iconProps.$themeColor]) {
      // If you need to use a color that doesn't match the text, use a defined theme color
      fill = iconProps.theme.designSystem.iconColors[iconProps.$themeColor];
    } else if (iconProps.color) {
      // This option is deprecated:
      fill = iconProps.color;
    }

    return css`
      && {
        height: ${size};
        width: ${size};
      }
      &&,
      g,
      mask,
      path,
      use {
        fill: ${fill};
      }
    `;
  }};

  path[d='M0 0h24v24H0z'] {
    display: none;
  }
  :hover {
    ${(iconProps) => {
      if (iconProps.$hoverThemeColor && iconProps.theme.designSystem.iconColors[iconProps.$hoverThemeColor]) {
        return css`
          &&,
          g,
          mask,
          path,
          use {
            fill: ${iconProps.theme.designSystem.iconColors[iconProps.$hoverThemeColor]};
          }
        `;
      }
      return '';
    }}
  }
`;

class ErrorBoundary extends React.Component<any, any> {
  static getDerivedStateFromError(): ErrorBoundraryState {
    return { hasError: true };
  }

  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static componentDidCatch(error, errorInfo): void {
    console.error(error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <svg
          stroke="currentColor"
          fill="currentColor"
          viewBox="0 0 24 24"
          height="1em"
          width="1em"
          xmlns="http://www.w3.org/2000/svg"
          aria-labelledby="icon-unable-to-load"
        >
          <title id="icon-unable-to-load">Unable to load icon</title>
          <path fill="none" d="M0 0h24v24H0V0z" />
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5v-4.58l.99.99 4-4 4 4 4-3.99L19 12.43V19zm0-9.41l-1.01-1.01-4 4.01-4-4-4 4-.99-1V5h14v4.59z" />
        </svg>
      );
    }

    return this.props.children;
  }
}

const Icon: FC<IconProps> = ({
  name,
  color,
  inheritColor,
  themeColor,
  hoverThemeColor,
  size,
  height,
  width,
  alt,
  animation,
  className,
  ...props
}) => {
  const preparedName = name?.toLowerCase()?.replace(/(?!^)_/g, '-');
  const IconToRender = loadable((prop) => import(/* webpackChunkName: "icon-" */ `./assets/${(prop as any).name}.svg`));

  return (
    <ColouredIcon
      {...props}
      $inheritColor={inheritColor}
      color={color}
      $themeColor={themeColor}
      $hoverThemeColor={hoverThemeColor}
      $size={size}
      $height={height}
      $width={width}
      component={<IconToRender name={preparedName} {...props} title={alt} />}
      data-level={themeColor}
      className={animation ? `cn_animate-${animation} ${className ?? ''}` : className}
    />
  );
};

const IconWithErrorBoundary: FC<IconProps> = (props) => {
  return (
    <ErrorBoundary>
      <Icon {...props} />
    </ErrorBoundary>
  );
};

export default IconWithErrorBoundary;
